/**
 * DEV-1413 (slice 2) — pre-auth geography declaration tree client.
 *
 * Fetches GET /api/CommunityMetadata/entities/declaration-tree (anonymous,
 * geo_* only, minimal payload per SCOPING_COMMUNITY_MEMBERSHIP.md §8.11
 * ruling 2) and maps it into the snake_case entity/relationship shapes the
 * canonical geo search helpers (`src/utils/geoLocationSearch.ts`) and
 * `<GeoLocationPicker>` consume.
 *
 * The payload includes INACTIVE geos by design — the declaration reads the
 * geography tree, not the community-pages list (§8.4 ruling 2). Landing
 * resolves at runtime to the nearest active ancestor; the declared row is
 * stored as-is (§8.4 ruling 1).
 */

import { API_BASE_URL } from '../../api/client';

export interface DeclarationTreeNode {
  entityGuid: string;
  name: string;
  entityType: string;
  isActive: boolean;
  parentEntityGuid: string | null;
}

/** Snake-case shapes compatible with geoLocationSearch / entityPath. */
export interface DeclarationPickerData {
  entities: {
    entity_guid: string;
    name: string;
    entity_type: string;
    is_active: boolean;
  }[];
  relationships: {
    parent_entity_guid: string;
    child_entity_guid: string;
    relationship_type: string;
  }[];
}

export function declarationTreeToPickerData(
  nodes: DeclarationTreeNode[],
): DeclarationPickerData {
  const entities: DeclarationPickerData['entities'] = [];
  const relationships: DeclarationPickerData['relationships'] = [];
  for (const n of nodes || []) {
    if (!n?.entityGuid) continue;
    entities.push({
      entity_guid: n.entityGuid,
      name: n.name || '',
      entity_type: n.entityType || '',
      is_active: n.isActive !== false,
    });
    if (n.parentEntityGuid) {
      relationships.push({
        parent_entity_guid: n.parentEntityGuid,
        child_entity_guid: n.entityGuid,
        relationship_type: 'parent_of',
      });
    }
  }
  return { entities, relationships };
}

/**
 * Anonymous fetch (no auth header) — the signup form runs pre-auth. Returns
 * empty picker data on any failure so the form degrades gracefully rather
 * than blocking signup.
 */
export async function fetchDeclarationPickerData(): Promise<DeclarationPickerData> {
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/CommunityMetadata/entities/declaration-tree`,
    );
    if (!res.ok) return { entities: [], relationships: [] };
    const nodes = (await res.json()) as DeclarationTreeNode[];
    return declarationTreeToPickerData(nodes);
  } catch {
    return { entities: [], relationships: [] };
  }
}
